/**
 * Transaction edit/create dialog provider. Any descendant can open the
 * dialog with `useTransactionEdit().openEdit(accountId, txId)` or
 * `useTransactionEdit().openCreate(defaultAccountId?)`; the dialog itself is
 * `TransactionForm`.
 */

import type { Account } from '@luminaria/shared';
import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { TransactionForm } from './TransactionForm';
import type { TransactionFormMode } from './transactionPayload';

interface TxEditContext {
  openEdit: (accountId: string, txId: string) => void;
  openCreate: (defaultAccountId?: string) => void;
}

const TransactionEditContext = createContext<TxEditContext | undefined>(undefined);

export function useTransactionEdit() {
  const ctx = useContext(TransactionEditContext);
  if (!ctx) throw new Error('useTransactionEdit must be used within TransactionEditProvider');
  return ctx;
}

interface TransactionEditProviderProps {
  children?: React.ReactNode;
  /** Accounts available for create mode. Required to enable openCreate. */
  accounts?: Account[];
}

const NO_ACCOUNTS: Account[] = [];

export function TransactionEditProvider({ children, accounts = NO_ACCOUNTS }: TransactionEditProviderProps) {
  const [mode, setMode] = useState<TransactionFormMode | null>(null);
  const [editTarget, setEditTarget] = useState<{ accountId: string; txId: string } | null>(null);
  const [createDefaultAccountId, setCreateDefaultAccountId] = useState<string | undefined>(undefined);
  // Bumped on every open: the form remounts with empty fields.
  const [session, setSession] = useState(0);

  const openEdit = useCallback((accountId: string, txId: string) => {
    setSession(n => n + 1);
    setEditTarget({ accountId, txId });
    setMode('edit');
  }, []);

  const openCreate = useCallback((defaultAccountId?: string) => {
    setSession(n => n + 1);
    setEditTarget(null);
    setCreateDefaultAccountId(defaultAccountId);
    setMode('create');
  }, []);

  const handleClose = useCallback(() => {
    setMode(null);
    setEditTarget(null);
    setCreateDefaultAccountId(undefined);
  }, []);

  const contextValue = useMemo(() => ({ openEdit, openCreate }), [openEdit, openCreate]);

  return (
    <TransactionEditContext.Provider value={contextValue}>
      {children}
      <TransactionForm
        key={session}
        mode={mode}
        editTarget={editTarget}
        createDefaultAccountId={createDefaultAccountId}
        accounts={accounts}
        onClose={handleClose}
      />
    </TransactionEditContext.Provider>
  );
}
